// @flow

import _ from 'lodash';

import ko from 'rgs-knockout';
import type { Observable, PureComputed } from 'rgs-knockout';
import kv from 'rgs-knockout-validation';

import { JSableOmitPrivate } from 'jsable';

import { Address, AddressLite } from 'kladr-address-model';

import { momentReprExtend } from 'ko-extender-repr-moment';
import { phone10DigitsViewExtend } from 'ko-extender-view-phone-10-digits';
import { mobilePhoneToJSMap } from 'ko-extender-to-from-js-phone';
import { OPFS_OAO, OPFS_OOO, OPFS_PAO, OPFS_ZAO } from 'known-opf';
import { JuridicalPersonIdentityDocument } from './document';

const phoneToFromJSExtend = {
	toJSMap: mobilePhoneToJSMap,
	fromJSMap: p => {
		if (!p) {
			return p;
		}
		p = (String(p)).replace(/\D+/g, '');
		if (p.length === 10) {
			p = '7' + p;
		}
		if (p.length === 11) {
			p = '+' + p;
		}
		return p;
	},
};

export class Person extends JSableOmitPrivate {
	FirstName: Observable<?string>;
	LastName: Observable<?string>;
	MiddleName: Observable<?string>;
	FullName: PureComputed<?string>;
	BirthDay: Observable<?string>;
	Email: Observable<?string>;
	Phone: Observable<?string>;
	Registration: Address;

	_model: any;

	constructor (options: any = {}, model: any) {
		super();

		this._model = model;

		this.FirstName = ko.observable()
			.extend({ toFromJS: true });

		this.LastName = ko.observable()
			.extend({ toFromJS: true });

		this.MiddleName = ko.observable()
			.extend({ toFromJS: true });

		this.FullName = ko.pureComputed(() => {
			const [first, last, middle]
					  = [ this.FirstName(), this.LastName(), this.MiddleName() ];
			return (last || '')
				+ ' '
				+ (first || '')
				+ (middle ? ' ' + middle : '');
		});

		this.BirthDay = ko.observable()
			.extend({ toFromJS: true })
			.extend(momentReprExtend);

		this.Email = ko.observable()
			.extend({ toFromJS: true });

		this.Phone = ko.observable() // todo: переделать телефон на observable-mobile-phone
			.extend(phone10DigitsViewExtend)
			.extend(phoneToFromJSExtend);

		this.Registration = this._newAddress({
			dictionaries: options.dictionaries,
			context: options.context,
		}, this._model);
	}

	fromJS (js: Object, ...rest: any) {
		super.fromJS(js, ...rest);
	}

	validationArray () {
		return _.filter(this, kv.utils.isValidatable);
	}

	_newAddress (options: any, model: any) {
		return new AddressLite(options, model);
	}
}

export class JuridicalPerson extends JSableOmitPrivate {
	constructor (options: any = {}, model: any) {
		super();

		this._model = model;

		this.Email = ko.observable()
			.extend({ toFromJS: true });

		this.Phone = ko.observable() // todo: переделать телефон на observable-mobile-phone
			.extend(phone10DigitsViewExtend)
			.extend(phoneToFromJSExtend);

		this.BusinessEntityTypeID = ko.observable()
			.extend({ toFromJS: true });

		this.OpfID = this.BusinessEntityTypeID;

		this.BusinessEntityTypeOptionsShort = ko.pureComputed(() => {
			return _([
				OPFS_OOO, // ООО - Общество с ограниченной ответственностью
				OPFS_PAO, // ПАО - Публичное акционерное общество
				OPFS_ZAO, // ЗАО - Закрытое акционерное общество
				OPFS_OAO, // ОАО - Открытое акционерное общество
			])
				.map(id => options.dictionaries.businessEntityTypes.get(id))
				.filter()
				.value();
		});

		this.BusinessEntityTypeOptionsFull = ko.pureComputed(() => {
			return _.sortBy(options.dictionaries.businessEntityTypes.values(), 'shortName');
		});

		this.Name = ko.observable()
			.extend({ toFromJS: true });

		this.FullName = ko.pureComputed(() => {
			const businessTypeID = this.BusinessEntityTypeID();
			const businessTypeName = _.get(
				options.dictionaries.businessEntityTypes.get(businessTypeID),
				'name'
			);

			const name = this.Name();

			return (businessTypeName ? (businessTypeName + ' ') : '') + name;
		});

		this.INN = ko.observable()
			.extend({ toFromJS: true });


		this.IdentityDocument = new JuridicalPersonIdentityDocument(options);

		this.Registration = this._newAddress({
			dictionaries: options.dictionaries,
		}, this._model);
	}

	_businessEntityTypeOptionsText ({ name, shortName }) {
		name = _.trim(name);
		shortName = _.trim(shortName);

		if (name === shortName) {
			return name;
		}

		if (!name) {
			return shortName;
		}

		if (!shortName) {
			return name;
		}

		return `${shortName} — ${name}`;
	}


	_newAddress (...args) {
		return new Address(...args);
	}
}
